import Grid2 from '@mui/material/Unstable_Grid2';

import Icon from '@/components/Icon/Icon';
import GridContainer2 from '@/components/Layout/GridContainer2/GridContainer2';
import SectionHeader from '@/components/SectionHeader/SectionHeader';
import type { Contentful, IconNameContent } from '@/contentful';
import useGlobalStyles from '@/styles/global.styles';
import { brandColors } from '@/styles/sunrunTheme.styles';

import useStyles from './HighlightIconStat.styles';
import StatCardContent from './StatCardContent/StatCardContent';

export interface HighlightIconCardContent extends Contentful {
  fields: {
    icon: IconNameContent;
    cardTitle: string;
    subTitle: string;
  };
}

export interface HighlightStatCardContent extends Contentful {
  fields: {
    stat?: string;
    cardTitle: string;
    subTitle: string;
  };
}

export interface HighlightIconStatContent extends Contentful {
  fields: {
    eyebrow?: string;
    title: string;
    highlightIconTextCards?: HighlightIconCardContent[];
    highlightStatTextCards?: HighlightStatCardContent[];
    subheading?: string;
    darkMode: boolean;
  };
}

interface Props {
  content: HighlightIconStatContent;
}

export default function HighlightIconStat({
  content: {
    fields: {
      eyebrow,
      title,
      highlightIconTextCards,
      highlightStatTextCards,
      subheading,
      darkMode,
    },
  },
}: Props) {
  const { classes: globalClasses } = useGlobalStyles({ fixed: true });
  const { classes, cx } = useStyles({ isOnDark: darkMode });

  const shouldRenderHeaderSub =
    (highlightIconTextCards?.length ?? 0) > 2 ||
    (highlightStatTextCards?.length ?? 0) > 2;

  return (
    <div className={cx(classes.container, { darkMode })}>
      <GridContainer2 fixed>
        <Grid2 xs={12} md={6}>
          <SectionHeader
            eyebrow={eyebrow}
            heading={title}
            subheading={shouldRenderHeaderSub ? subheading : undefined}
            isOnDark={darkMode}
            fixed={true}
          />
        </Grid2>
      </GridContainer2>
      <GridContainer2 fixed>
        {highlightIconTextCards &&
          !highlightStatTextCards &&
          highlightIconTextCards.map((card, index) => {
            const { icon, cardTitle, subTitle } = card.fields;

            return (
              <Grid2
                xs={6}
                md={highlightIconTextCards.length === 3 ? 4 : 3}
                key={`${cardTitle}-${
                  // biome-ignore lint/suspicious/noArrayIndexKey: Automated migration to Biome
                  index
                }`}
                className={classes.card}
              >
                <div className={classes.cardContent}>
                  <div className={classes.icon}>
                    <Icon
                      name={icon.fields.name || ''}
                      color={darkMode ? brandColors.white : brandColors.black}
                      width={56}
                    />
                  </div>
                  <div className={classes.cardCopy}>
                    <StatCardContent
                      heading={cardTitle}
                      subheading={subTitle}
                      isOnDark={darkMode}
                      fixed={true}
                    />
                  </div>
                </div>
              </Grid2>
            );
          })}
        {highlightStatTextCards &&
          !highlightIconTextCards &&
          highlightStatTextCards.map((card, index) => {
            const { stat, cardTitle, subTitle } = card.fields;

            return (
              <Grid2
                xs={6}
                md={highlightStatTextCards.length === 3 ? 4 : 3}
                key={`${cardTitle}-${
                  // biome-ignore lint/suspicious/noArrayIndexKey: Automated migration to Biome
                  index
                }`}
                className={classes.card}
              >
                <div className={cx(classes.cardContent, { darkMode })}>
                  <div
                    className={cx(classes.cardCopy, { darkMode })}
                    data-testid='stat-card-copy'
                  >
                    <p className={cx(classes.stat, globalClasses.h2)}>{stat}</p>
                    <p className={classes.statTitle}>{cardTitle}</p>
                    <p
                      className={cx(classes.statSubTitle, globalClasses.body, {
                        darkMode,
                      })}
                    >
                      {subTitle}
                    </p>
                  </div>
                </div>
              </Grid2>
            );
          })}
      </GridContainer2>
    </div>
  );
}
